import { useEffect } from 'react';
import { Layout3Root } from './Layout3Root';
import './styles.css';
import { LayoutHeader as Header } from '@/layouts';
import { useTheme } from '@/contexts/ThemeContext';
import { LayoutFooter as Footer } from '@/layouts';
import { ServiceModal } from '@/components/ServiceModal';
import { Button } from '@/components/ui/button';
import { useAppData } from '@/contexts/AppDataContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  CreditCard,
  Shield,
  FileText,
  Video,
  MessageCircle,
  Wrench,
  TrendingUp,
  Search,
  Users,
  Package,
  Settings,
  Gauge,
  Handshake,
  HandCoins,
  Car,
} from 'lucide-react';

const serviceInfo: Record<string, { icon: typeof Video; title: string; description: string; color: string }> = {
  '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', color: 'bg-blue-500' },
  '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', color: 'bg-green-500' },
  '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', color: 'bg-purple-500' },
  '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', color: 'bg-orange-500' },
  '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', color: 'bg-teal-500' },
  '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', color: 'bg-blue-600' },
  '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', color: 'bg-gray-600' },
  '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', color: 'bg-slate-600' },
  '65dcb7238eca108d87c91dba': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', color: 'bg-red-500' },
  '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', color: 'bg-indigo-600' },
  '65dcb7fe8eca108d87c91dbe': { icon: TrendingUp, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', color: 'bg-cyan-500' },
  '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', color: 'bg-indigo-500' },
  '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', color: 'bg-green-600' },
};

const getServiceInfo = (serviceId: string) => {
  return serviceInfo[serviceId] || {
    icon: MessageCircle,
    title: 'Serviço',
    description: 'Entre em contato para mais informações sobre este serviço',
    color: 'bg-gray-500',
  };
};

const getServiceButtonText = (serviceId: string) => {
  const buttonTexts: Record<string, string> = {
    '65da42048eca108d87c91da6': 'Agendar',
    '65dceeac8eca108d87c91dc6': 'Agendar',
    '65dcef098eca108d87c91dc7': 'Agendar',
    '65dcb5e78eca108d87c91db8': 'Agendar',
    '65dcb7898eca108d87c91dbc': 'Simular',
    '65dcb7548eca108d87c91dbb': 'Avaliar',
    '65dcb7c58eca108d87c91dbd': 'Avaliar',
    '65dcb7238eca108d87c91dba': 'Cotar',
    '65dcb6528eca108d87c91db9': 'Cotar',
    '6628fd535bda2ae51f6fc5ff': 'Cotar',
    '65dcef468eca108d87c91dc8': 'Solicitar',
    '65dcb8548eca108d87c91dbf': 'Solicitar',
    '65dcb7fe8eca108d87c91dbe': 'Consultar',
  };
  return buttonTexts[serviceId] || 'Solicitar';
};

const Services = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const services = store.services || [];
  const { colors } = useTheme();
  const buttonColor = colors.button;
  const primaryColor = colors.primary;

  useEffect(() => {
    document.title = `Serviços - ${config.name}`;
  }, [config.name]);

  return (
    <Layout3Root withHeader>
      <Header config={config} />

      {/* Mesma seção de serviços da home, expandida com todos os itens */}
      <section className="wl3-section wl3-section-muted">
        <div className="wl3-container">
          <div className="wl3-section-header">
            <div>
              <p className="wl3-eyebrow">Serviços</p>
              <h1 className="wl3-h2 mt-1">Tudo para fechar negócio</h1>
              <p className="wl3-lead mt-1 max-w-lg text-sm">
                Financiamento, avaliação e muito mais em um só lugar.
              </p>
            </div>
          </div>

          {services.length === 0 ? (
            <div className="wl3-service-tile text-center py-10">
              <MessageCircle className="h-8 w-8 mx-auto mb-3 text-[var(--wl3-muted)] opacity-50" />
              <p className="font-semibold text-[var(--wl3-text)]">Nenhum serviço disponível no momento</p>
              <p className="text-sm text-[var(--wl3-muted)] mt-1">Entre em contato para saber como podemos ajudar.</p>
            </div>
          ) : (
            <div className="wl3-service-grid">
              {services.map((service: { service: string }, index: number) => {
                const info = getServiceInfo(service.service);
                const IconComponent = info.icon;

                return (
                  <div key={`${service.service}-${index}`} className="wl3-service-tile">
                    <div
                      className="wl3-trust-icon"
                      style={{ backgroundColor: `${buttonColor}12`, color: buttonColor }}
                    >
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-[var(--wl3-text)]">{info.title}</h3>
                    <p className="text-sm text-[var(--wl3-muted)] line-clamp-3 flex-grow">{info.description}</p>
                    <ServiceModal service={service} serviceTitle={info.title} config={config}>
                      <Button
                        className="w-full rounded-lg text-white font-semibold h-10"
                        style={{ backgroundColor: buttonColor }}
                      >
                        {getServiceButtonText(service.service)}
                      </Button>
                    </ServiceModal>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Diferenciais — mesmo bloco da home */}
      <section className="wl3-section wl3-section-muted pt-0">
        <div className="wl3-container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="wl3-eyebrow">Por que comprar aqui</p>
            <h2 className="wl3-h2 mt-2">Compra com confiança</h2>
          </div>
          <div className="wl3-trust-grid">
            {[
              { icon: Shield, title: 'Veículos revisados', desc: 'Inspeção e histórico transparente' },
              { icon: CreditCard, title: 'Financiamento', desc: 'Condições especiais e aprovação ágil' },
              { icon: Car, title: 'Estoque completo', desc: 'Seminovos e zero KM disponíveis' },
              { icon: MessageCircle, title: 'Atendimento humano', desc: 'Equipe pronta no WhatsApp e telefone' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="wl3-trust-item">
                <div
                  className="wl3-trust-icon"
                  style={{ backgroundColor: `${buttonColor}12`, color: buttonColor }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-[var(--wl3-text)] mb-1">{title}</h3>
                <p className="text-sm text-[var(--wl3-muted)]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — mesmo padrão da home */}
      <section className="wl3-section bg-white pb-16">
        <div className="wl3-container">
          <div className="wl3-cta" style={{ backgroundColor: primaryColor }}>
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                  Precisa de ajuda com algum serviço?
                </h2>
                <p className="text-white/85 mt-2 max-w-md">
                  Fale com nossa equipe agora e receba atendimento personalizado.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Button
                  size="lg"
                  className="rounded-lg bg-white font-semibold h-12 px-6 hover:bg-white/90"
                  style={{ color: buttonColor }}
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-lg font-semibold h-12 px-6 border-white/30 text-white bg-white/10 hover:bg-white/20 hover:text-white"
                  onClick={() => { window.location.href = `tel:${store.phone?.formatted}`; }}
                >
                  Ligar agora
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer config={config} />
    </Layout3Root>
  );
};

export default Services;
